import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { Alert } from './ui/Alert';
import { Sheet, SheetBody, SheetContent, SheetFooter, SheetTrigger } from './ui/Sheet';
import { HtmlPreview } from './html-template/HtmlPreview';
import { apiFetch } from '../lib/api';
import { blocksToHtml } from '../lib/blocks';
import { fillPreviewMergeFields } from '../lib/previewMerge';
import { useFooterDefaults } from '../lib/useFooterDefaults';
import {
  LIBRARY_GROUPS,
  TEMPLATE_LIBRARY,
  libraryByGroup,
  type LibraryTemplate,
} from '../lib/templateLibrary';
import { ApiError, type Template } from '../types/api';

/**
 * The template library: ready-made layouts grouped by purpose. Picking one
 * shows a preview; "Use this template" only saves a draft copy as a normal
 * template and opens it. Nothing is sent or scheduled from here.
 */
export function TemplateLibrarySheet() {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>(TEMPLATE_LIBRARY[0]!.id);
  const [name, setName] = useState(TEMPLATE_LIBRARY[0]!.name);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const footerDefaults = useFooterDefaults();

  const selected: LibraryTemplate =
    TEMPLATE_LIBRARY.find((t) => t.id === selectedId) ?? TEMPLATE_LIBRARY[0]!;
  const previewHtml = useMemo(
    () => fillPreviewMergeFields(blocksToHtml(selected.build(footerDefaults))),
    [selected, footerDefaults],
  );

  const mutation = useMutation({
    mutationFn: () => {
      const blocks = selected.build(footerDefaults);
      return apiFetch<{ template: Template }>('/templates', {
        method: 'POST',
        body: { name: name.trim(), blockJson: { blocks }, html: blocksToHtml(blocks) },
      });
    },
    onSuccess: ({ template }) => {
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      setOpen(false);
      navigate(`/skabeloner/${template.id}`);
    },
  });

  const error =
    mutation.error instanceof ApiError && mutation.error.body.message
      ? mutation.error.body.message
      : mutation.isError
        ? 'The template could not be created.'
        : null;

  const choose = (t: LibraryTemplate) => {
    setSelectedId(t.id);
    setName(t.name);
    mutation.reset();
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) mutation.reset();
      }}
    >
      <SheetTrigger asChild>
        <Button variant="secondary">Template library</Button>
      </SheetTrigger>
      <SheetContent
        title="Template library"
        description="Ready-made layouts with placeholder text. Using one creates a draft copy you can edit. Nothing is sent."
      >
        <form
          className="flex min-h-0 flex-grow flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <SheetBody>
            {error ? <Alert>{error}</Alert> : null}
            {LIBRARY_GROUPS.map((g) => (
              <section key={g.id} aria-label={g.label} className="flex flex-col gap-2">
                <div>
                  <h3 className="text-sm font-semibold">{g.label}</h3>
                  <p className="text-xs text-muted">{g.hint}</p>
                </div>
                <ul className="flex flex-col gap-1.5 p-0 list-none m-0">
                  {libraryByGroup(g.id).map((t) => (
                    <li key={t.id}>
                      <button
                        type="button"
                        aria-pressed={t.id === selected.id}
                        onClick={() => choose(t)}
                        className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${
                          t.id === selected.id
                            ? 'border-accent bg-accent-soft'
                            : 'border-border bg-card'
                        }`}
                      >
                        <span className="block font-medium">{t.name}</span>
                        <span className="block text-xs text-muted">{t.description}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold">Preview</h3>
              <HtmlPreview
                html={previewHtml}
                title={`Preview of ${selected.name}`}
                className="h-[420px]"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="library-template-name">Template name</Label>
              <Input
                id="library-template-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </SheetBody>
          <SheetFooter>
            <Button type="submit" disabled={mutation.isPending || name.trim() === ''}>
              {mutation.isPending ? 'Creating…' : 'Use this template'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
